import { WHITE_NOTES, noteName, notePitchName } from "./notes.js";

export const PIANO_START_MIDI = 48;
export const PIANO_NOTE_COUNT = 25;
const PIANO_WHITE_KEY_COUNT = 15;
const BLACK_KEY_WIDTH_PERCENT = 5.25;
const DEFAULT_KEYBOARD_START_MIDI = 48;
const PIANO_KEYBOARD_CODES = [
  "KeyA", "KeyW", "KeyS", "KeyE", "KeyD", "KeyF", "KeyT", "KeyG", "KeyY", "KeyH", "KeyU", "KeyJ",
  "KeyK", "KeyO", "KeyL", "KeyP", "Semicolon", "Quote",
];
const PIANO_BINDINGS = new WeakMap();

export function twoOctaveNotes(startMidi = PIANO_START_MIDI, count = PIANO_NOTE_COUNT) {
  return Array.from({ length: count }, (_unused, index) => startMidi + index);
}

function blackOffsetForMidi(midi, startMidi = PIANO_START_MIDI) {
  let whiteIndex = 0;
  for (let noteMidi = startMidi; noteMidi < midi; noteMidi += 1) {
    if (WHITE_NOTES.has(notePitchName(noteName(noteMidi)))) {
      whiteIndex += 1;
    }
  }
  return ((whiteIndex / PIANO_WHITE_KEY_COUNT) * 100) - (BLACK_KEY_WIDTH_PERCENT / 2);
}

export function renderPiano(container, onClick, options = {}) {
  if (!container) {
    return;
  }
  const startMidi = options.startMidi || PIANO_START_MIDI;
  const count = options.count || PIANO_NOTE_COUNT;
  const notes = twoOctaveNotes(startMidi, count);
  const whites = document.createElement("div");
  whites.className = "piano-whites";
  const blacks = [];
  const triggerNote = (button) => {
    const midi = Number(button?.dataset.midi);
    if (!Number.isInteger(midi)) {
      return;
    }
    onClick?.(midi, button);
  };

  notes.forEach((midi) => {
    const label = noteName(midi);
    const pitch = notePitchName(label);
    const button = document.createElement("button");
    button.type = "button";
    button.className = `piano-key ${WHITE_NOTES.has(pitch) ? "white" : "black"}`;
    button.dataset.midi = String(midi);
    button.dataset.pitch = pitch;
    button.textContent = label;
    button.setAttribute("aria-label", label);
    button.addEventListener("click", (event) => {
      if (event.detail === 0) {
        triggerNote(button);
      }
    });
    if (WHITE_NOTES.has(pitch)) {
      whites.append(button);
    } else {
      button.style.left = `${blackOffsetForMidi(midi, startMidi)}%`;
      blacks.push(button);
    }
  });

  container.replaceChildren(whites, ...blacks);
  container.tabIndex = container.tabIndex >= 0 ? container.tabIndex : 0;
  container.style.touchAction = "none";
  bindPianoControls(container, triggerNote, startMidi, count);
}

function bindPianoControls(container, triggerNote, startMidi, count) {
  let binding = PIANO_BINDINGS.get(container);
  if (binding) {
    binding.triggerNote = triggerNote;
    binding.startMidi = startMidi;
    binding.count = count;
    return;
  }

  binding = {
    activePointerId: null,
    count,
    lastMidi: null,
    octaveOffset: 0,
    startMidi,
    triggerNote,
  };
  PIANO_BINDINGS.set(container, binding);

  const keyFromPoint = (event) => {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    return element?.closest?.(".piano-key");
  };
  const triggerFromEvent = (event) => {
    const keyElement = keyFromPoint(event);
    if (!keyElement || !container.contains(keyElement)) {
      return;
    }
    const midi = Number(keyElement.dataset.midi);
    if (midi === binding.lastMidi) {
      return;
    }
    binding.lastMidi = midi;
    binding.triggerNote(keyElement);
  };

  container.addEventListener("pointerdown", (event) => {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }
    binding.activePointerId = event.pointerId;
    binding.lastMidi = null;
    container.focus({ preventScroll: true });
    event.preventDefault();
    triggerFromEvent(event);
  });
  container.addEventListener("pointermove", (event) => {
    if (event.pointerId !== binding.activePointerId) {
      return;
    }
    event.preventDefault();
    triggerFromEvent(event);
  });
  ["pointerup", "pointercancel", "lostpointercapture"].forEach((eventName) => {
    container.addEventListener(eventName, (event) => {
      if (event.pointerId === binding.activePointerId) {
        binding.activePointerId = null;
        binding.lastMidi = null;
      }
    });
  });
  container.addEventListener("keydown", (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.repeat) {
      return;
    }
    if (event.code === "KeyZ" || event.code === "KeyX") {
      event.preventDefault();
      binding.octaveOffset += event.code === "KeyZ" ? -12 : 12;
      return;
    }
    const keyIndex = PIANO_KEYBOARD_CODES.indexOf(event.code);
    if (keyIndex === -1) {
      return;
    }
    const midi = DEFAULT_KEYBOARD_START_MIDI + binding.octaveOffset + keyIndex;
    if (midi < binding.startMidi || midi >= binding.startMidi + binding.count) {
      return;
    }
    const keyElement = container.querySelector(`.piano-key[data-midi="${midi}"]`);
    if (!keyElement) {
      return;
    }
    event.preventDefault();
    binding.triggerNote(keyElement);
  });
}

export function highlightPiano(container, midis, rootMidi = midis[0]) {
  const highlighted = new Set(midis);
  container?.querySelectorAll(".piano-key").forEach((keyElement) => {
    const midi = Number(keyElement.dataset.midi);
    keyElement.classList.toggle("is-root", midi === rootMidi);
    keyElement.classList.toggle("is-highlighted", highlighted.has(midi));
  });
}

export function flashPianoKey(container, midiOrPitch) {
  const selector = Number.isInteger(midiOrPitch)
    ? `.piano-key[data-midi="${midiOrPitch}"]`
    : `.piano-key[data-pitch="${escapeAttributeValue(midiOrPitch)}"]`;
  container?.querySelectorAll(selector).forEach((keyElement) => {
    keyElement.classList.remove("is-flashing");
    void keyElement.offsetWidth;
    keyElement.classList.add("is-flashing");
    window.setTimeout(() => keyElement.classList.remove("is-flashing"), 220);
  });
}

function escapeAttributeValue(value) {
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
